import { readSheet } from 'read-excel-file/browser';
import writeExcelFile from 'write-excel-file/browser';
import type { TemplateImport } from '@pcp/contracts';

/**
 * Planilha → linhas de texto separadas por `;` (o que o ABL espera em `POST /imports`).
 * Números saem com vírgula decimal (pt-BR), que é como a validação do Datasul os lê; datas, em ISO.
 */
const cellToText = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'number') return String(v).replace('.', ',');
  if (typeof v === 'boolean') return v ? '1' : '0';
  return String(v)
    .replace(/[\r\n;]+/g, ' ')
    .trim();
};

/** CSV do Excel brasileiro costuma ser windows-1252; tenta UTF-8 e cai para ele se houver bytes inválidos. */
export function decodeText(buf: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf).replace(/^\uFEFF/, '');
  } catch {
    return new TextDecoder('windows-1252').decode(buf);
  }
}

export function csvToLines(text: string): string[] {
  return text.split(/\r?\n/).filter((l) => l.replace(/;/g, '').trim() !== '');
}

/** Lê `.xlsx` (primeira planilha) ou `.csv`/`.txt` e devolve as linhas em texto, cabeçalho incluso. */
export async function fileToLines(file: File): Promise<string[]> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx')) {
    const rows = await readSheet(file);
    return rows
      .map((row) => row.map(cellToText).join(';'))
      .filter((line) => line.replace(/;/g, '').trim() !== '');
  }
  if (name.endsWith('.csv') || name.endsWith('.txt')) {
    return csvToLines(decodeText(await file.arrayBuffer()));
  }
  throw new Error('Formato não suportado. Envie um arquivo .xlsx ou .csv (separado por ponto e vírgula).');
}

/**
 * Planilha → objetos `{ "<cabeçalho>": "<texto>" }` (a primeira linha é o cabeçalho). Datas viram ISO e números
 * saem como texto; a validação e a conversão ficam no BFF.
 */
export async function fileToRecords(file: File): Promise<Record<string, string>[]> {
  if (!file.name.toLowerCase().endsWith('.xlsx')) {
    throw new Error('Formato não suportado. Envie o arquivo .xlsx da planilha de importação.');
  }
  const rows = await readSheet(file);
  const [head, ...body] = rows;
  if (!head) return [];
  const keys = head.map((h) => (h === null || h === undefined ? '' : String(h)));
  const asText = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    return String(v).trim();
  };
  return body
    .map((row) => Object.fromEntries(keys.map((k, i) => [k, asText(row[i])]).filter(([k]) => k !== '')))
    .filter((r) => Object.values(r).some((v) => v !== ''));
}

/** Modelo `.xlsx`: aba "Modelo" só com o cabeçalho (nada de linha de exemplo para não virar dado) e aba "Instruções". */
export async function buildTemplateBlob(template: TemplateImport): Promise<Blob> {
  const header = template.colunas.map((c) => ({ value: c.nome, fontWeight: 'bold' as const }));
  const instructions = [
    [
      { value: 'Coluna', fontWeight: 'bold' as const },
      { value: 'Obrigatória', fontWeight: 'bold' as const },
      { value: 'Exemplo', fontWeight: 'bold' as const },
      { value: 'Descrição', fontWeight: 'bold' as const },
    ],
    ...template.colunas.map((c) => [
      { value: c.nome },
      { value: c.obrigatorio ? 'Sim' : 'Não' },
      { value: c.exemplo },
      { value: c.descricao },
    ]),
  ];
  return writeExcelFile([
    { sheet: 'Modelo', data: [header] },
    { sheet: 'Instruções', data: instructions },
  ]).toBlob();
}

/** Baixa um `Blob` com o nome informado. */
export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
